import {ChangeDetectionStrategy, Component, OnDestroy, OnInit} from '@angular/core';
import {CurrentSkedTaskDetailInfoComponent} from '../task-info/current-sked-task-detail-info.component';
import {AsyncPipe} from '@angular/common';
import {Store, select} from '@ngrx/store';
import {ActivatedRoute} from '@angular/router';
import {BehaviorSubject, Observable, Subscription, combineLatest, filter, map, take, withLatestFrom} from 'rxjs';
import {CurrentSkedTask, Subtask} from 'src/app/core/model/task';
import {Project} from 'src/app/core/model/project';
import {RewardCoin} from 'src/app/core/model/reward';
import {selectCurrentSkedDetailTask} from 'src/app/core/store/current-sked/current-sked.selector';
import {selectProjectsMap} from 'src/app/core/store/projects/projects.selector';
import {selectCoinTypesMap} from 'src/app/core/store/rewards/rewards.selector';
import {selectRouterParam} from 'src/app/core/store/router/router.selector';
import {GetProjectsAction} from 'src/app/core/store/projects/projects.action';
import {GetCoinTypesAction} from 'src/app/core/store/rewards/rewards.action';
import {GetCurrentSkedTaskAction} from 'src/app/core/store/current-sked/current-sked.action';
import {MessageService} from 'src/app/services/message.service';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';

@Component({
  selector: 'current-sked-task-detail-mobile-detail',
  standalone: true,
  imports: [CurrentSkedTaskDetailInfoComponent, AsyncPipe, OphBackButtonComponent, OphLoadingModule],
  templateUrl: './current-sked-task-detail-mobile-detail.component.html',
  styleUrl: './current-sked-task-detail-mobile-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentSkedTaskDetailMobileDetailComponent implements OnInit, OnDestroy {
  task$: Observable<CurrentSkedTask> = this.store$.pipe(select(selectCurrentSkedDetailTask));
  projectsMap$: Observable<Record<string, Project>> = this.store$.pipe(select(selectProjectsMap));
  coinTypesMap$: Observable<Record<string, RewardCoin>> = this.store$.pipe(select(selectCoinTypesMap));
  taskId$: Observable<string> = this.store$.pipe(select(selectRouterParam('taskId')));
  subtaskId$: Observable<string> = this.store$.pipe(select(selectRouterParam('subtaskId')));

  subtask$: Observable<Subtask> = this.task$.pipe(
    filter(task => !!task),
    map(task => {
      const subtaskId = this.activatedRoute.snapshot.params['subtaskId'];
      return task.subtasks.find(subtask => subtask._id === subtaskId);
    })
  );

  loading$ = new BehaviorSubject<boolean>(true);

  private subscriptions = new Subscription();

  constructor(
    private store$: Store,
    private activatedRoute: ActivatedRoute,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.store$.dispatch(new GetProjectsAction({}));
    this.store$.dispatch(new GetCoinTypesAction({}));
    this.subscriptions.add(this.subscribeToTaskId());
    this.subscriptions.add(
      combineLatest([
        this.task$.pipe(filter(task => !!task)),
        this.projectsMap$.pipe(filter(map => Object.keys(map).length > 0)),
        this.coinTypesMap$.pipe(filter(map => Object.keys(map).length > 0)),
      ]).subscribe({
        next: () => {
          this.loading$.next(false);
        },
        error: error => {
          console.error('Error loading data:', error);
          this.loading$.next(false);
        },
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  subscribeToTaskId(): Subscription {
    return this.taskId$
      .pipe(
        filter(id => !!id),
        withLatestFrom(this.task$),
        take(1)
      )
      .subscribe(([taskId, task]) => {
        if (!task) {
          this.store$.dispatch(
            new GetCurrentSkedTaskAction({
              taskId,
              onFailure: () => this.onGetTaskFailure(),
            })
          );
        }
      });
  }

  onGetTaskFailure() {
    this.messageService.add('There was a problem getting this task.');
  }

  onBack() {
    window.history.back();
  }
}
